<template>
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Team</div>
                <div class="page-bar-main">Meeting Templates</div>
            </div>
            <div style="display:flex;align-items:center">
                <button type="button" class="btn-light" @click="$router.push('/meetings')">Back</button>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <div class="form-wrapper template-page-wrapper">
                <div style="margin-bottom:20px">
                    Templates show up when someone clicks <b>+ New Meeting</b>. Picking one prefills the meeting's name, board columns (with any default items below), and agenda sections - the color is only used for its dot in that list.
                </div>

                <div v-if="!templates.length && !loading" class="meeting-menu-empty" style="margin-bottom:20px">No templates yet - add one below.</div>

                <div v-for="(t, i) in templates" :key="t.ID || `new-${i}`" class="template-item">
                    <div class="template-item-head" @click="toggleOpen(i)">
                        <span class="template-item-name">{{ t.Name || '(untitled template)' }}</span>
                        <div class="template-item-head-right">
                            <svg @click.stop="onDeleteTemplateClick(t)" class="template-delete-icon" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm6 16.538l-4.592-4.548 4.546-4.587-1.416-1.403-4.545 4.589-4.588-4.543-1.405 1.405 4.593 4.552-4.547 4.592 1.405 1.405 4.555-4.596 4.591 4.55 1.403-1.416z"/></svg>
                            <span class="template-chevron" :class="openIndex == i ? 'open' : ''">&#9656;</span>
                        </div>
                    </div>

                    <div v-if="openIndex == i" class="template-item-body">
                        <h3 class="meeting-form-label" style="margin-top:0">Template name</h3>
                        <input type="text" v-model="t.Name" placeholder="Template name" />

                        <div class="meeting-form-row">
                            <div>
                                <h3 class="meeting-form-label">Description</h3>
                                <input type="text" v-model="t.Description" placeholder="Shown under the name in the picker" />
                            </div>
                            <div style="flex:0 0 auto">
                                <h3 class="meeting-form-label">Color</h3>
                                <input type="color" v-model="t.Color" />
                            </div>
                        </div>

                        <h3 class="meeting-form-label">Board columns <span class="meeting-form-hint">(with any default items to start the board with)</span></h3>
                        <div class="template-col-block" v-for="(col, ci) in t.Columns" :key="ci">
                            <div class="template-col-row">
                                <input type="text" v-model="col.name" placeholder="Column name" />
                                <!-- The palette, or a custom color, as in Edit Columns on a board -->
                                <div class="crm-board-color-select">
                                    <select v-model="col.color" aria-label="Column color">
                                        <option v-for="(color, k) in $store.state.colorPalette" :key="k" :value="color.hex">{{ color.label }}</option>
                                    </select>
                                    <input type="color" v-model="col.color" title="Custom color" />
                                </div>
                                <svg-icon icon="x" class="template-remove" @click="col.items.length ? (columnPendingRemoval = { t, col }) : t.Columns.splice(ci, 1)" />
                            </div>
                            <div class="template-col-items">
                                <div class="template-col-item-row" v-for="(item, ii) in col.items" :key="ii">
                                    <input type="text" v-model="item.name" placeholder="Item name" />
                                    <input type="text" v-model="item.description" placeholder="Description" />
                                    <select v-model="item.priority">
                                        <option :value="null">No Priority</option>
                                        <option value="high">High</option>
                                        <option value="med">Medium</option>
                                        <option value="low">Low</option>
                                    </select>
                                    <svg-icon icon="x" class="template-remove" @click="col.items.splice(ii, 1)" />
                                </div>
                                <div class="template-addlink template-addlink-sm" @click="col.items.push({ name: '', description: '', priority: null })">+ Add Item</div>
                            </div>
                        </div>
                        <div class="template-col-links">
                            <span class="template-addlink" @click="t.Columns.push({ name: '', color: NEW_COLUMN_COLOR, show: true, items: [] })">+ Add Column</span>
                            <!-- Same as Edit Columns on a board: from the last column (Done) backwards -->
                            <span v-if="t.Columns.length" class="template-addlink" @click="applyDefaultColumnColors(t.Columns, $store.state.colorPalette)">Apply Default Colors</span>
                        </div>

                        <h3 class="meeting-form-label">Sections <span class="meeting-form-hint">(set a time limit for each)</span></h3>
                        <!-- Dragged by the handle to reorder, as Board Templates' columns -->
                        <draggable v-model="t.Sections" handle=".template-section-handle">
                        <div class="template-section-row" v-for="(section, si) in t.Sections" :key="si">
                            <div class="template-section-top">
                                <span class="template-section-handle" title="Drag to reorder">&#8942;&#8942;</span>
                                <div class="template-section-num">{{ si + 1 }}</div>
                                <input type="text" v-model="section.name" placeholder="Section name" />
                                <svg-icon icon="x" class="template-remove" @click="t.Sections.splice(si, 1)" />
                            </div>
                            <div class="template-section-bottom">
                                <div class="template-section-time">
                                    <div class="meeting-form-sublabel">Time Limit</div>
                                    <div class="template-section-time-input">
                                        <input type="number" min="0" v-model.number="section.minutes" />
                                        <span>min</span>
                                    </div>
                                </div>
                                <div class="template-section-desc">
                                    <div class="meeting-form-sublabel">Description</div>
                                    <input type="text" v-model="section.description" placeholder="Description" />
                                </div>
                            </div>
                            <!-- While this section is on in Run Meeting: scorecard / rocks above the board (team meetings only), or no board -->
                            <div class="template-section-shows">
                                <label><input type="checkbox" v-model="section.showScorecard" />Show the team's scorecard</label>
                                <label><input type="checkbox" v-model="section.showRocks" />Show the team's rocks</label>
                                <label><input type="checkbox" v-model="section.hideBoard" />Hide the board</label>
                            </div>
                        </div>
                        </draggable>
                        <div class="template-addlink" @click="t.Sections.push(newSection())">+ Add Section</div>

                        <input :class="t._saving ? 'disable-button' : ''" type="submit" value="Save Template" @click="saveTemplate(t)" />
                    </div>
                </div>

                <div style="display:flex">
                    <div class="add-btn" @click="addTemplate()">+ Add Template</div>
                </div>
            </div>
        </div>

        <confirm-delete v-if="columnPendingRemoval" @confirm="removeColumnConfirmed()" @cancel="() => { columnPendingRemoval = null; }" msg="This column has default items on it - remove it and its items?"/>
        <confirm-delete v-if="templatePendingDelete" @confirm="deleteTemplateConfirmed()" @cancel="() => { templatePendingDelete = null; }" :msg="deleteTemplateConfirmMsg"/>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import SvgIcon from '@/components/SvgIcon.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import { VueDraggableNext } from 'vue-draggable-next';
import { NEW_COLUMN_COLOR, applyDefaultColumnColors } from '@/columnColors';
import { parseSections, newSection } from '@/meetingSections';
import { primaryColor } from '@/primaryColor';

export default defineComponent({
    name: 'MeetingTemplates',
    components: {
        SvgIcon,
        ConfirmDelete,
        draggable: VueDraggableNext
    },
    data() {
        return {
            NEW_COLUMN_COLOR,
            templates: [] as any,
            loading: true,
            openIndex: null as number | null, // only one template's editor is expanded at a time
            columnPendingRemoval: null as any, // { t, col } awaiting confirmation
            templatePendingDelete: null as any // the template object awaiting confirmation
        }
    },
    computed: {
        deleteTemplateConfirmMsg(): string {
            return this.templatePendingDelete ? `Delete the "${this.templatePendingDelete.Name}" template? This cannot be undone.` : '';
        }
    },
    async created() {
        await this.getTemplates();
        this.$store.state.routeLoaded = true;
    },
    methods: {
        newSection,
        applyDefaultColumnColors,
        async getTemplates() {
            this.loading = true;
            await axios.get(`${this.$store.state.apiUrl}/api/meetingtemplates`)
                .then(resp => {
                    this.templates = resp.data.map((t: any) => ({
                        ...t,
                        Columns: t.ColumnsJSON
                            ? JSON.parse(t.ColumnsJSON).map((c: any) => ({ ...c, items: c.items || [] }))
                            : [],
                        Sections: parseSections(t.SectionsJSON),
                        _saving: false
                    }));
                })
                .catch(e => {
                    console.log(e);
                })
            this.loading = false;
        },
        toggleOpen(i: number) {
            this.openIndex = this.openIndex === i ? null : i;
        },
        addTemplate() {
            this.templates.push({
                ID: null,
                Name: '',
                Description: '',
                Color: primaryColor(),
                Columns: [],
                Sections: [],
                _saving: false
            });
            this.openIndex = this.templates.length - 1;
        },
        removeColumnConfirmed() {
            if(this.columnPendingRemoval) {
                const { t, col } = this.columnPendingRemoval;
                const ci = t.Columns.indexOf(col);
                if(ci > -1) {
                    t.Columns.splice(ci, 1);
                }
            }
            this.columnPendingRemoval = null;
        },
        async saveTemplate(t: any) {
            if(!t.Name || !String(t.Name).trim()) {
                alert('Include a name for this template');
                return;
            }

            t._saving = true;

            const data = {
                ID: t.ID,
                Name: t.Name,
                Description: t.Description,
                Color: t.Color,
                Columns: t.Columns,
                Sections: t.Sections
            }

            await axios.post(`${this.$store.state.apiUrl}/api/meetingtemplates${t.ID ? '/details' : ''}`, data)
                .then(async resp => {
                    if(!t.ID) {
                        t.ID = resp.data.ID;
                    }
                    this.openIndex = null;
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error saving this template');
                })

            t._saving = false;
        },
        onDeleteTemplateClick(t: any) {
            if(!t.ID) {
                // never saved - just drop it locally, no confirmation needed
                this.removeTemplateLocally(t);
                return;
            }
            this.templatePendingDelete = t;
        },
        removeTemplateLocally(t: any) {
            const i = this.templates.indexOf(t);
            if(i === -1) {
                return;
            }
            this.templates.splice(i, 1);
            if(this.openIndex === i) {
                this.openIndex = null;
            } else if(this.openIndex !== null && this.openIndex > i) {
                this.openIndex -= 1;
            }
        },
        async deleteTemplateConfirmed() {
            const t = this.templatePendingDelete;
            this.templatePendingDelete = null;
            if(!t) {
                return;
            }

            await axios.delete(`${this.$store.state.apiUrl}/api/meetingtemplates/${t.ID}`)
                .then(() => {
                    this.removeTemplateLocally(t);
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error deleting this template');
                })
        }
    }
});
</script>

<style scoped>
.template-page-wrapper { max-width: 700px; width: 700px; }
.template-item { border: 1px solid var(--mid-gray); border-radius: 10px; padding: 15px 20px; margin-bottom: 15px; }

.template-item-head { display: flex; align-items: center; justify-content: space-between; cursor: pointer; }
.template-item-name { font-weight: 700; font-size: 15px; }
.template-item-head-right { display: flex; align-items: center; gap: 14px; }
.template-delete-icon { color: red; width: 18px; height: 18px; cursor: pointer; flex-shrink: 0; }
.template-chevron { display: inline-block; color: #a6a29d; transition: transform .15s; }
.template-chevron.open { transform: rotate(90deg); }

.template-item-body { margin-top: 18px; }

.meeting-form-label { margin-top: 20px; margin-bottom: 8px; font-size: 14px; }
.meeting-form-label:first-of-type { margin-top: 0; }
.meeting-form-hint { font-weight: normal; color: #a6a29d; font-size: 12px; }
.meeting-form-sublabel { font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: #a6a29d; margin-bottom: 4px; }
.meeting-form-row { display: flex; gap: 15px; }
.meeting-form-row > div { flex: 1; }

.template-addlink { color: #646464; font-weight: bold; font-size: 13px; cursor: pointer; margin: 0 0 15px; }
.template-addlink:hover { color: var(--primary-color); }
.template-addlink-sm { font-size: 12px; margin-bottom: 15px; }
.template-remove { width: 14px !important; height: 14px !important; cursor: pointer; color: #a6a29d; flex-shrink: 0; }

.template-col-block { border: 1px solid #E9E7E4; border-radius: 10px; padding: 12px 14px; margin-bottom: 10px; }
.template-col-row { display: flex; align-items: center; gap: 10px; }
.template-col-row input[type=text] { flex: 1; margin-bottom: 0; }
.template-col-row .crm-board-color-select select, .template-col-row .crm-board-color-select input[type=color] { margin-bottom: 0; }
.template-col-links { display: flex; flex-wrap: wrap; gap: 18px; margin: 0 0 15px; }
.template-col-links .template-addlink { margin: 0; }
.template-col-items { margin-top: 10px; padding-left: 10px; border-left: 2px solid #F1EFEC; }
.template-col-item-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.template-col-item-row input[type=text] { flex: 1; margin-bottom: 0; }
.template-col-item-row select { width: 130px; flex-shrink: 0; margin-bottom: 0; }

.template-section-row { border: 1px solid #E9E7E4; border-radius: 10px; padding: 12px 14px; margin-bottom: 10px; }
.template-section-top { display: flex; align-items: center; gap: 10px; }
.template-section-top input { margin-bottom: 0; flex: 1; }
.template-section-handle { cursor: move; color: #a6a29d; letter-spacing: -3px; font-size: 13px; padding: 0 4px 0 0; user-select: none; flex-shrink: 0; }
.template-section-num { width: 22px; height: 22px; border-radius: 50%; background: #F1EFEC; color: #646464; font-weight: 800; font-size: 11px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.template-section-bottom { display: flex; gap: 16px; margin-top: 10px; }
.template-section-time input { margin-bottom: 0; width: 70px; }
.template-section-time-input { display: flex; align-items: center; gap: 6px; }
.template-section-time-input span { font-size: 13px; color: #646464; }
.template-section-desc { flex: 1; }
.template-section-desc input { margin-bottom: 0; width: 100%; }
.template-section-shows { display: flex; flex-wrap: wrap; gap: 6px 18px; margin-top: 10px; }
.template-section-shows label { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; color: #646464; cursor: pointer; }
.template-section-shows input[type=checkbox] { width: 15px; height: 15px; margin: 0; accent-color: var(--primary-color); cursor: pointer; }
</style>
